import React, { useState, useEffect } from "react";
import { X, Upload, AlertCircle, Image as ImageIcon, Type } from "lucide-react";
import {
  Popup,
  CreatePopupRequest,
  UpdatePopupRequest,
} from "../../types/landing";
import { uploadService } from "../../services/uploadService";
import { toast } from "react-hot-toast";

interface PopupFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePopupRequest | UpdatePopupRequest) => Promise<void>;
  popup?: Popup | null;
  mode: "create" | "edit";
}

const PopupFormModal: React.FC<PopupFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  popup,
  mode,
}) => {
  const [formData, setFormData] = useState<CreatePopupRequest>({
    title: "",
    content: "",
    contentType: "text",
    image: { url: "", alt: "" },
    buttonText: "",
    buttonLink: "",
    status: "draft",
    displayDuration: 5,
    delayBeforeShow: 2000,
    showOnce: true,
    priority: 0,
  });

  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  useEffect(() => {
    if (popup && mode === "edit") {
      setFormData({
        title: popup.title,
        content: popup.content || "",
        contentType: popup.contentType,
        image: popup.image || { url: "", alt: "" },
        buttonText: popup.buttonText || "",
        buttonLink: popup.buttonLink || "",
        status: popup.status,
        displayDuration: popup.displayDuration,
        delayBeforeShow: popup.delayBeforeShow,
        showOnce: popup.showOnce,
        expiryDate: popup.expiryDate
          ? new Date(popup.expiryDate).toISOString().split("T")[0]
          : undefined,
        priority: popup.priority,
      });
      if (popup.image?.url) {
        setImagePreview(popup.image.url);
      }
      setSelectedImageFile(null);
    } else {
      // Reset form for create mode
      setFormData({
        title: "",
        content: "",
        contentType: "text",
        image: { url: "", alt: "" },
        buttonText: "",
        buttonLink: "",
        status: "draft",
        displayDuration: 5,
        delayBeforeShow: 2000,
        showOnce: true,
        priority: 0,
      });
      setImagePreview("");
      setSelectedImageFile(null);
    }
  }, [popup, mode, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name.startsWith("image.")) {
      const imageField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        image: {
          ...prev.image,
          [imageField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
            ? parseFloat(value)
            : value,
      }));
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Store the file for later upload
    setSelectedImageFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (formData.contentType === "text" && !formData.content?.trim()) {
      toast.error("Content is required for text popups");
      return;
    }

    if (
      formData.contentType === "image" &&
      !formData.image?.url &&
      !selectedImageFile
    ) {
      toast.error("Image is required for image popups");
      return;
    }

    try {
      setUploading(true);
      let finalFormData = { ...formData };

      // Upload image if a new file was selected
      if (selectedImageFile && formData.contentType === "image") {
        const uploadResponse = await uploadService.uploadFile(
          selectedImageFile,
          "course-resources/popups"
        );

        if (!uploadResponse.success) {
          toast.error(uploadResponse.message || "Failed to upload image");
          setUploading(false);
          return;
        }

        // Update form data with uploaded image URL
        finalFormData = {
          ...finalFormData,
          image: {
            ...finalFormData.image,
            url: uploadResponse.data?.url || "",
          },
        };
      }

      await onSubmit(finalFormData);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to save popup");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {mode === "create" ? "Create New Popup" : "Edit Popup"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter popup title"
              required
            />
          </div>

          {/* Content Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, contentType: "text" }))
                }
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition ${
                  formData.contentType === "text"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <Type size={20} />
                <span className="font-medium">Text</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, contentType: "image" }))
                }
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition ${
                  formData.contentType === "image"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <ImageIcon size={20} />
                <span className="font-medium">Image</span>
              </button>
            </div>
          </div>

          {/* Text Content */}
          {formData.contentType === "text" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter popup content"
                required={formData.contentType === "text"}
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.content?.length || 0}/500 characters
              </p>
            </div>
          )}

          {/* Image Upload */}
          {formData.contentType === "image" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {imagePreview && (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview("");
                        setSelectedImageFile(null);
                        setFormData((prev) => ({
                          ...prev,
                          image: { ...prev.image, url: "" },
                        }));
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer transition">
                    <Upload size={18} />
                    <span>Select Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {selectedImageFile && (
                    <span className="text-sm text-gray-600">
                      {selectedImageFile.name}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Image will be uploaded when you save the popup
                </p>
                <input
                  type="text"
                  name="image.alt"
                  value={formData.image?.alt || ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Image alt text (optional)"
                />
              </div>
            </div>
          )}

          {/* Button Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Text
              </label>
              <input
                type="text"
                name="buttonText"
                value={formData.buttonText}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Learn More"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button Link
              </label>
              <input
                type="url"
                name="buttonLink"
                value={formData.buttonLink}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Display Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Duration (seconds)
              </label>
              <input
                type="number"
                name="displayDuration"
                value={formData.displayDuration}
                onChange={handleInputChange}
                min="1"
                max="60"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delay Before Show (ms)
              </label>
              <input
                type="number"
                name="delayBeforeShow"
                value={formData.delayBeforeShow}
                onChange={handleInputChange}
                min="0"
                max="30000"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Additional Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <input
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Higher priority popups are shown first
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date
              </label>
              <input
                type="date"
                name="expiryDate"
                value={
                  formData.expiryDate
                    ? new Date(formData.expiryDate).toISOString().split("T")[0]
                    : ""
                }
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              name="showOnce"
              checked={formData.showOnce}
              onChange={handleInputChange}
              className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="text-sm text-gray-700">
              Show only once per session
            </label>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="text-blue-500 flex-shrink-0" size={20} />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Important Note:</p>
              <p>
                Only one published popup will be shown at a time. If multiple
                popups are published, the one with the highest priority will be
                displayed.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mode === "create" ? "Create Popup" : "Update Popup"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PopupFormModal;
